// My wallet (wave 3, W2 look): the balance card with Add to wallet and
// Withdraw under it, then the wallet history in the modern table (status as
// an icon and a word, Addis Ababa 12-hour dates, CSV and print).
//
// Money, exactly as before:
// - Add to wallet: the amount, then the payment provider; "Pay now" posts
//   the same form to the provider's link (order_id wallet-<uid>-XXXX, amount,
//   currency, product_name, first_name, last_name, quantity, cust_id,
//   mobile_no, email, source, selectedProviderIndex).
// - Withdraw (customers only when settings.RiderWithDraw; everyone else
//   always): more than 0 and no more than the balance, then
//   withdrawBalance(profile, amount) — the server checks and debits. New: a
//   question naming the amount first, and a message once the request is in.
// - A profile without a phone, email or first name is sent to the profile
//   first, as before. Payment methods and the history are fetched once on
//   arrival only when the providers are not loaded yet, as before.
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SouthWestRoundedIcon from '@mui/icons-material/SouthWestRounded';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { tokens as c } from '../theme';
import { showToast, confirmDialog } from '../components/feedback';
import WalletCard from '../components/WalletCard';
import { DataTable, FieldLabel, FieldError, calmCaps, formatMoney, formatNumber } from '../components/modern';

const icons = {
  paypal: require('../assets/payment-icons/paypal-logo.png').default,
  braintree: require('../assets/payment-icons/braintree-logo.png').default,
  stripe: require('../assets/payment-icons/stripe-logo.png').default,
  paytm: require('../assets/payment-icons/paytm-logo.png').default,
  payulatam: require('../assets/payment-icons/payulatam-logo.png').default,
  flutterwave: require('../assets/payment-icons/flutterwave-logo.png').default,
  paystack: require('../assets/payment-icons/paystack-logo.png').default,
  securepay: require('../assets/payment-icons/securepay-logo.png').default,
  payfast: require('../assets/payment-icons/payfast-logo.png').default,
  liqpay: require('../assets/payment-icons/liqpay-logo.png').default,
  culqi: require('../assets/payment-icons/culqi-logo.png').default,
  mercadopago: require('../assets/payment-icons/mercadopago-logo.png').default,
  squareup: require('../assets/payment-icons/squareup-logo.png').default,
  wipay: require('../assets/payment-icons/wipay-logo.png').default,
  test: require('../assets/payment-icons/test-logo.png'),
  razorpay: require('../assets/payment-icons/razorpay-logo.png').default,
  paymongo: require('../assets/payment-icons/paymongo-logo.png').default,
  iyzico: require('../assets/payment-icons/iyzico-logo.png').default,
  slickpay: require('../assets/payment-icons/slickpay-logo.png').default,
  xendit: require('../assets/payment-icons/xendit-logo.png').default,
  tap: require('../assets/payment-icons/tap-logo.png').default,
  chapa: require('../assets/payment-icons/chapa-logo.png'),
  telebirr: require('../assets/payment-icons/telebirr-logo.png'),
  santimpay: require('../assets/payment-icons/santimpay-logo.png'),
  arifpay: require('../assets/payment-icons/arifpay-logo.png'),
};

// The history's type, as before: anything "credit" is credited, anything
// "withdraw" is a withdrawal, the rest is debited.
const typeOf = (raw) => {
  const s = String(raw || '');
  if (s.includes('Credit') || s.includes('credit')) return 'credited';
  if (s.includes('Withdraw') || s.includes('withdraw')) return 'withdraw';
  return 'debited';
};

const UserWallet = () => {
  const { withdrawBalance } = api;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const providers = useSelector((state) => state.paymentmethods.providers);
  const profile = auth.profile || null;

  const [amount, setAmount] = useState(0);
  const [amountText, setAmountText] = useState('');
  const [amountError, setAmountError] = useState(null);
  const [modalInfo, setModalInfo] = useState({ mOpen: false, mType: null });
  const [selectedProvider, setSelectedProvider] = useState();
  const [selectedProviderIndex, setSelectedProviderIndex] = useState(0);
  const [paymentModalStatus, setPaymentModalStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });

  useEffect(() => {
    if (providers) {
      setSelectedProvider(providers[0]);
    }
  }, [providers]);

  // The history as rows: new objects (the store's copy is no longer
  // changed in place), newest first.
  const data = useMemo(() => {
    const wdata = auth.walletHistory;
    const wallHis = [];
    for (let key in wdata) {
      if (!wdata[key]) continue;
      wallHis.push({ ...wdata[key], walletKey: key, type: typeOf(wdata[key].type) });
    }
    return wallHis.reverse();
  }, [auth.walletHistory]);

  // Fetch payment methods if providers are empty (once, on arrival)
  useEffect(() => {
    if (!providers || providers.length === 0) {
      setIsLoading(true);
      Promise.all([dispatch(api.fetchPaymentMethodsPromise()), dispatch(api.fetchWalletHistoryPromise())]).finally(() => setIsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const profileComplete = () => !!(profile && profile.mobile && profile.mobile.length > 6 && profile.email && profile.firstName);

  const askToCompleteProfile = async () => {
    const go = await confirmDialog({
      title: t('profile_incomplete'),
      confirmLabel: L('goto', 'Go to profile'),
      cancelLabel: calmCaps(t('close')),
    });
    if (go) navigate('/profile');
  };

  const openAmount = (mType) => {
    setAmount(0);
    setAmountText('');
    setAmountError(null);
    setModalInfo({ mOpen: true, mType });
  };

  const doRecharge = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!profileComplete()) {
      askToCompleteProfile();
    } else {
      if (isLoading) {
        showToast.info(t('loading'));
        return;
      }
      if (providers) {
        openAmount('add');
      } else {
        showToast.error(t('provider_not_found'));
      }
    }
  };

  const doWithdraw = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!profileComplete()) {
      askToCompleteProfile();
    } else {
      if (parseFloat(profile.walletBalance) > 0) {
        openAmount('withdraw');
      } else {
        showToast.warning(t('wallet_bal_low'));
      }
    }
  };

  const cancelOperation = () => {
    setAmount(0);
    setAmountText('');
    setAmountError(null);
    setModalInfo({ mOpen: false, mType: null });
  };

  const money = (v) => formatMoney(v || 0, settings, true);

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    // !(amount > 0) also refuses NaN (typed text): `NaN <= 0` is false, so a
    // non-number used to reach the withdraw request, whose write then threw.
    if (!(amount > 0)) {
      setAmountError(t('valid_amount'));
      return;
    }
    if (modalInfo.mType === 'add') {
      setModalInfo({ ...modalInfo, mOpen: false });
      setPaymentModalStatus(true);
      return;
    }
    // The request only asks: the server checks the amount against the
    // balance again and makes the debit (withdrawCreate); the page never
    // writes a balance. A missing balance counts as 0 here.
    if (!((parseFloat(profile.walletBalance) || 0) >= amount)) {
      setAmountError(t('valid_amount'));
      return;
    }
    const requested = amount;
    const ok = await confirmDialog({
      title: L('wallet_withdraw_confirm_title', 'Withdraw {{amount}}?', { amount: money(requested) }),
      body: L('wallet_withdraw_confirm_body', 'Finance checks the request, then pays it out.'),
      confirmLabel: calmCaps(t('withdraw')),
      cancelLabel: L('confirm_cancel', 'Cancel'),
    });
    if (!ok) return;
    const sent = dispatch(withdrawBalance(profile, requested));
    cancelOperation();
    Promise.resolve(sent).then(
      () => showToast.success(L('wallet_withdraw_sent', 'Withdrawal request sent for {{amount}}', { amount: money(requested) })),
      (err) => showToast.error((err && err.message) || t('withdraw_failed'))
    );
  };

  const createOrderId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const reference = [...Array(4)].map(() => chars[~~(Math.random() * chars.length)]).join('');
    return 'wallet-' + auth.profile.uid + '-' + reference;
  };

  // "Pay now" submits the form to the provider; the dialog closes a moment
  // later (as before), once the browser has sent it.
  const handlePaymentSubmitted = () => {
    setTimeout(() => {
      setPaymentModalStatus(false);
    }, 1500);
  };

  // ── Balance ──────────────────────────────────────────────────────────
  const hasBalance = !!profile && Object.prototype.hasOwnProperty.call(profile, 'walletBalance');
  const balanceNumber = hasBalance ? formatMoney(profile.walletBalance, settings) : formatMoney(0, settings);
  // The symbol before the amount when swipe_symbol is off, after it otherwise (as before).
  const balanceText = settings.swipe_symbol === false ? `${settings.symbol} ${balanceNumber}` : `${balanceNumber} ${settings.symbol}`;
  const canWithdraw = (settings && settings.RiderWithDraw && auth.profile.usertype === 'customer') || auth.profile.usertype !== 'customer';

  // ── History ──────────────────────────────────────────────────────────
  const typeStatus = (row) => {
    if (row.type === 'credited') return { tone: 'success', icon: <SouthWestRoundedIcon />, label: calmCaps(t('credited')) };
    if (row.type === 'debited') return { tone: 'error', icon: <NorthEastRoundedIcon />, label: calmCaps(t('debited')) };
    return { tone: 'warning', icon: <AccountBalanceOutlinedIcon />, label: calmCaps(t(row.type)) };
  };
  const txTitle = String(t('transaction_id')).replace(/\s*:\s*$/, '');
  const columns = [
    { id: 'date', title: t('requestDate'), field: 'date', type: 'datetime', defaultSort: 'desc' },
    {
      id: 'amount',
      title: t('amount'),
      field: 'amount',
      type: 'money',
      render: (rowData) => money(rowData.amount),
      exportValue: (rowData) => rowData.amount,
    },
    {
      id: 'transaction_id',
      title: txTitle,
      field: 'transaction_id',
      render: (rowData) => (rowData.transaction_id ? rowData.transaction_id : rowData.txRef) || null,
      searchValue: (rowData) => `${rowData.transaction_id || ''} ${rowData.txRef || ''}`,
      exportValue: (rowData) => (rowData.transaction_id ? rowData.transaction_id : rowData.txRef) || '',
      cellSx: { overflowWrap: 'anywhere' },
    },
    {
      id: 'type',
      title: t('type'),
      field: 'type',
      type: 'status',
      status: typeStatus,
      sortValue: (row) => typeStatus(row).label,
      searchValue: (row) => typeStatus(row).label,
      exportValue: (row) => typeStatus(row).label,
    },
  ];
  const historyTitle = calmCaps(t('transaction_history_title'));

  const providerList = Array.isArray(providers) ? providers : [];

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <WalletCard
        title={t('Balance')}
        amount={balanceText}
        sx={{ maxWidth: { md: 560 } }}
        actions={
          <>
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={doRecharge} sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
              {t('add_to_wallet')}
            </Button>
            {canWithdraw ? (
              <Button variant="outlined" color="neutral" startIcon={<PaymentsOutlinedIcon />} onClick={doWithdraw} sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
                {calmCaps(t('withdraw'))}
              </Button>
            ) : null}
          </>
        }
      />

      <DataTable
        title={historyTitle}
        rows={data}
        columns={columns}
        rowKey="walletKey"
        loading={isLoading && data.length === 0}
        countText={({ shown, total, query, loading }) =>
          loading
            ? L('loading', 'Loading…')
            : query
              ? L('table_count_shown', '{{shown}} of {{total}}', { shown, total })
              : t('wallet_tx_count', {
                  count: total,
                  n: formatNumber(total),
                  defaultValue_one: '{{n}} transaction',
                  defaultValue_other: '{{n}} transactions',
                  defaultValue: '{{n}} transactions',
                })
        }
        subtitle={historyTitle}
        prefsKey="user_wallet"
        exportCsv
        exportPrint
        exportFileName="wallet_history"
        exportTitle={historyTitle}
        emptyState={({ query }) =>
          query
            ? null
            : {
                icon: <ReceiptLongOutlinedIcon />,
                title: L('wallet_tx_empty', 'No wallet transactions yet'),
                body: L('wallet_tx_empty_body', 'Money added to or taken from this wallet appears here.'),
              }
        }
        minWidth={620}
      />

      {/* The amount: to add (then the payment provider) or to withdraw */}
      <Dialog
        open={modalInfo.mOpen}
        onClose={cancelOperation}
        maxWidth="xs"
        fullWidth
        fullScreen={phone}
        aria-labelledby="wallet-amount-title"
        PaperProps={{ component: 'form', noValidate: true, onSubmit: handleSubmit }}
      >
        <DialogTitle id="wallet-amount-title" sx={{ pr: 8 }}>
          {modalInfo.mType === 'add' ? t('add_to_wallet') : calmCaps(t('withdraw'))}
          <IconButton aria-label={L('close_dialog', 'Close')} onClick={cancelOperation} sx={{ position: 'absolute', right: 12, top: 12 }}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <FieldLabel htmlFor="wallet-amount">{t('amount')}</FieldLabel>
          <TextField
            id="wallet-amount"
            name="amount"
            fullWidth
            autoFocus
            value={amountText}
            error={!!amountError}
            onChange={(e) => {
              const text = e.target.value;
              setAmountText(text);
              setAmountError(null);
              // The number shown is the number used: thousands commas are
              // dropped (the old box dropped them as they were typed), and
              // anything else that is not a number is refused on submit
              // (parseFloat read "1,000" as 1 and "12abc" as 12).
              const clean = text.replace(/,/g, '').trim();
              setAmount(clean === '' ? 0 : Number(clean));
            }}
            inputProps={{ inputMode: 'decimal', autoComplete: 'off', 'aria-describedby': amountError ? 'wallet-amount-error' : undefined }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Box component="span" sx={{ color: c.INK_2, fontWeight: 600 }}>
                    {settings.symbol}
                  </Box>
                </InputAdornment>
              ),
            }}
            sx={{ '& input': { fontVariantNumeric: 'tabular-nums' } }}
          />
          <FieldError id="wallet-amount-error">{amountError}</FieldError>
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="neutral" onClick={cancelOperation}>
            {L('confirm_cancel', 'Cancel')}
          </Button>
          <Button type="submit" variant="contained">
            {modalInfo.mType === 'add' ? t('add_to_wallet') : calmCaps(t('withdraw'))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* The payment provider; Pay now posts the same form as before */}
      <Dialog open={paymentModalStatus} onClose={() => setPaymentModalStatus(false)} maxWidth="xs" fullWidth fullScreen={phone} aria-labelledby="wallet-pay-title">
        {providerList.length && selectedProvider && modalInfo.mType === 'add' && amount > 0 ? (
          <form action={selectedProvider.link} method="POST">
            <input type="hidden" name="order_id" value={createOrderId()} />
            <input type="hidden" name="amount" value={amount} />
            <input type="hidden" name="currency" value={settings.code} />
            <input type="hidden" name="product_name" value={t('add_money')} />
            <input type="hidden" name="first_name" value={profile.firstName} />
            <input type="hidden" name="last_name" value={profile.lastName} />
            <input type="hidden" name="quantity" value={1} />
            <input type="hidden" name="cust_id" value={auth.profile.uid} />
            <input type="hidden" name="mobile_no" value={profile.mobile} />
            <input type="hidden" name="email" value={profile.email} />
            <input type="hidden" name="source" value={'DonkeyDriveAppWebView'} />
            <DialogTitle id="wallet-pay-title" sx={{ pr: 8 }}>
              {t('payment')}
              <IconButton aria-label={L('close_dialog', 'Close')} onClick={() => setPaymentModalStatus(false)} sx={{ position: 'absolute', right: 12, top: 12 }}>
                <CloseRoundedIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
              <Box sx={{ fontSize: 15, color: c.INK_2, mb: 2 }}>
                {t('amount')}:{' '}
                <Box component="span" sx={{ fontWeight: 700, color: c.INK, fontVariantNumeric: 'tabular-nums' }}>
                  {money(amount)}
                </Box>
              </Box>
              <FieldLabel id="selectedProviderIndex-label">{t('payment')}</FieldLabel>
              <TextField
                select
                fullWidth
                id="selectedProviderIndex"
                name="selectedProviderIndex"
                value={selectedProviderIndex}
                onChange={(e) => {
                  setSelectedProviderIndex(parseInt(e.target.value));
                  setSelectedProvider(providers[parseInt(e.target.value)]);
                }}
                SelectProps={{ labelId: 'selectedProviderIndex-label' }}
              >
                {providerList.map((provider, index) => (
                  <MenuItem key={provider.name} value={index} sx={{ gap: 1.5 }}>
                    {icons[provider.name] ? <Box component="img" src={icons[provider.name]} alt="" sx={{ height: 24, width: 'auto' }} /> : null}
                    <Box component="span" sx={{ textTransform: 'capitalize' }}>
                      {provider.name}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </DialogContent>
            <DialogActions>
              <Button variant="text" color="neutral" onClick={() => setPaymentModalStatus(false)}>
                {L('confirm_cancel', 'Cancel')}
              </Button>
              <Button variant="contained" color="success" type="submit" onClick={handlePaymentSubmitted}>
                {t('paynow_button')}
              </Button>
            </DialogActions>
          </form>
        ) : null}
      </Dialog>
    </Box>
  );
};

export default UserWallet;
